import { Button, ButtonGroup } from '@heroui/react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import _ from 'lodash';

import useSegment from '@/hooks/useSegment';

import { EntryList } from '../EntryList';

import { LineChart } from './LineChart';
import { DailyLineData } from './DailyLineData';
import { WeeklyLineData } from './WeeklyLineData';

const EVENT = 'Progress chart all entries viewed';

export function LineChartModal({ initMode = 'week', question }) {
  const { t } = useTranslation('progress');
  const { sendTrackEvent } = useSegment();
  const { pathname } = useLocation();

  const [mode, setMode] = useState(initMode);

  useEffect(() => {
    const eventData = {
      id: question.id,
      timeFrame: mode,
      pathname,
      procedureId: question.procedureId,
      procedureName: question.surveyName,
      questionType: question.type,
      title: question.title,
    };
    sendTrackEvent(EVENT, eventData);
  }, [mode]);

  const SelectedChart = useMemo(() => {
    switch (mode) {
      case 'day':
        return <DailyLineData question={question} />;

      case 'week':
        return <WeeklyLineData question={question} />;

      case 'all': {
        const { patientEntries } = question;
        const firstEntryDate = _.first(patientEntries).date;
        const isFirstEntry = _.every(patientEntries, ({ date }) =>
          dayjs(firstEntryDate).isSame(date, 'day')
        );
        const chartMode = isFirstEntry ? 'day' : 'all';

        return (
          <>
            <LineChart mode={chartMode} question={question} />
            {!question.metricTracker && (
              <EntryList question={question} mode={chartMode} />
            )}
          </>
        );
      }

      default:
        return null;
    }
  }, [mode, question]);

  return (
    <div className="space-y-4">
      <h1 className="font-semibold text-lg">{question.chartTitle}</h1>
      <h2 className="font-light">{question.unit}</h2>
      <ButtonGroup
        className="w-full"
        color="primary"
        variant="bordered"
        size="md"
      >
        <Button
          className="w-1/3"
          variant={mode === 'day' ? 'solid' : 'bordered'}
          onPress={() => setMode('day')}
        >
          {t('linechartview.tab.day')}
        </Button>
        <Button
          className="w-1/3"
          variant={mode === 'week' ? 'solid' : 'bordered'}
          onPress={() => setMode('week')}
        >
          {t('linechartview.tab.week')}
        </Button>
        <Button
          className="w-1/3"
          variant={mode === 'all' ? 'solid' : 'bordered'}
          onPress={() => setMode('all')}
        >
          {t('linechartview.tab.all')}
        </Button>
      </ButtonGroup>
      {SelectedChart}
    </div>
  );
}
